/**
 * M09 Knowledge 管理 fixture · S3 Day13
 */
import type {
	KnowledgeDocumentCreateRequest,
	KnowledgeDocumentItem,
	KnowledgeDocumentListResponse,
	KnowledgeIndexStatusResponse,
	KnowledgePrefetchMetricsResponse,
	KnowledgeSearchSandboxRequest,
	KnowledgeSearchSandboxResponse,
} from "@/lib/api/knowledge";

import { registerFixture } from "./registry";

const updatedAt = new Date("2026-05-01T00:00:00.000Z").toISOString();

let docs: KnowledgeDocumentItem[] = [
	{
		doc_id: "kd_shipping_seed",
		merchant_id: "mer_nY0bZmLI",
		namespace: "policy",
		title: "Shipping policy",
		content_type: "markdown",
		status: "indexed",
		body_preview: "物流政策：订单通常 2 个工作日内发货，发货后同步 tracking number。",
		chunk_count: 1,
		indexed_at: updatedAt,
		updated_by: "system",
		updated_at: updatedAt,
	},
];

let searchCount = 0;
let hitCount = 0;
let scoreSum = 0;

export function registerM09Fixtures(): void {
	registerFixture<KnowledgeIndexStatusResponse>({
		pathname: "/admin/v1/knowledge/index-status",
		methods: ["GET"],
		resolve: (ctx) => {
			const merchantId = ctx.query.merchant_id ?? "mer_nY0bZmLI";
			const localDocs = docs.filter((item) => item.merchant_id === merchantId);
			return {
				data: {
					merchant_id: merchantId,
					qdrant_enabled: false,
					qdrant_probe: false,
					local_document_count: localDocs.length,
					last_indexed_at: localDocs[0]?.indexed_at ?? null,
					collections: [
						{
							purpose: "products",
							collection_name: "products_dev",
							status: "not_configured",
							points_count: null,
							vectors_count: null,
							local_document_count: 0,
							message: "Qdrant URL/API key not configured in runtime env.",
						},
						{
							purpose: "documents",
							collection_name: "documents_dev",
							status: "not_configured",
							points_count: null,
							vectors_count: null,
							local_document_count: localDocs.length,
							message: "Qdrant URL/API key not configured in runtime env.",
						},
					],
				},
			};
		},
	});

	registerFixture<KnowledgeDocumentListResponse>({
		pathname: "/admin/v1/knowledge/documents",
		methods: ["GET"],
		resolve: (ctx) => {
			const merchantId = ctx.query.merchant_id ?? "mer_nY0bZmLI";
			const items = docs.filter((item) => item.merchant_id === merchantId);
			return { data: { items, total: items.length } };
		},
	});

	registerFixture<KnowledgeDocumentItem>({
		pathname: "/admin/v1/knowledge/documents",
		methods: ["POST"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as KnowledgeDocumentCreateRequest;
			const now = new Date().toISOString();
			const item: KnowledgeDocumentItem = {
				doc_id: `kd_mock_${docs.length + 1}`,
				merchant_id: body.merchant_id,
				namespace: body.namespace,
				title: body.title,
				content_type: body.content_type,
				status: "indexed",
				body_preview: body.body_text,
				chunk_count: Math.max(1, Math.ceil(body.body_text.length / 600)),
				indexed_at: now,
				updated_by: body.updated_by ?? "mock-admin",
				updated_at: now,
			};
			docs = [item, ...docs];
			return { delay_ms: 100, data: item };
		},
	});

	registerFixture<KnowledgeSearchSandboxResponse>({
		pathname: "/admin/v1/knowledge/search-sandbox",
		methods: ["POST"],
		resolve: (ctx) => {
			const body = (ctx.body ?? {}) as KnowledgeSearchSandboxRequest;
			const query = body.query.toLowerCase();
			const items = docs
				.filter((item) => item.merchant_id === body.merchant_id)
				.filter((item) => body.namespaces.includes(item.namespace))
				.map((item) => ({
					namespace: item.namespace,
					doc_id: item.doc_id,
					title: item.title,
					content: item.body_preview,
					score:
						item.title.toLowerCase().includes(query) ||
						item.body_preview.toLowerCase().includes(query)
							? 0.92
							: 0.41,
				}))
				.filter((item) => item.score >= 0.5)
				.slice(0, body.top_k);
			searchCount += 1;
			if (items.length > 0) hitCount += 1;
			scoreSum += items[0]?.score ?? 0;
			return {
				delay_ms: 120,
				data: {
					merchant_id: body.merchant_id,
					source: "local_lexical",
					total: items.length,
					items,
					audit_id: `ks_mock_${searchCount}`,
				},
			};
		},
	});

	registerFixture<KnowledgePrefetchMetricsResponse>({
		pathname: "/admin/v1/knowledge/prefetch-metrics",
		methods: ["GET"],
		resolve: (ctx) => {
			const merchantId = ctx.query.merchant_id ?? "mer_nY0bZmLI";
			const rate = searchCount > 0 ? hitCount / searchCount : 0;
			const average = searchCount > 0 ? scoreSum / searchCount : 0;
			return {
				data: {
					merchant_id: merchantId,
					window_hours: 6,
					latest_total_searches: searchCount,
					latest_hit_rate: Number(rate.toFixed(4)),
					buckets: Array.from({ length: 6 }).map((_, index) => ({
						hour_start: new Date(Date.now() - (5 - index) * 3600_000).toISOString(),
						total_searches: index === 5 ? searchCount : 0,
						hit_count: index === 5 ? hitCount : 0,
						hit_rate: index === 5 ? Number(rate.toFixed(4)) : 0,
						average_top_score: index === 5 ? Number(average.toFixed(4)) : 0,
					})),
				},
			};
		},
	});
}
